import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authControllerEnterDelegation } from './auth.api';
import { useSession } from './session-provider';
import { getAccessToken, setAccessToken } from '../../api/http-client';
import { contextQueryKeyPrefix } from '../context/context-query-keys';

/**
 * Enters an approved delegation as one atomic authorization-context transition.
 * The old token is restored if the server response or profile refresh fails.
 */
export function useDelegationEntry(): (delegationId: string) => Promise<void> {
  const queryClient = useQueryClient();
  const { refreshProfile } = useSession();

  return useCallback(async (delegationId: string) => {
    const previousToken = getAccessToken();
    try {
      const result = await authControllerEnterDelegation(delegationId);
      if (!result?.accessToken) {
        throw new Error('AUTH_DELEGATION_ENTRY_INVALID_RESPONSE');
      }

      await queryClient.cancelQueries({ queryKey: contextQueryKeyPrefix, exact: false });
      setAccessToken(result.accessToken);
      queryClient.removeQueries({ queryKey: contextQueryKeyPrefix, exact: false });
      queryClient.removeQueries({ queryKey: ['navigation'] });
      queryClient.removeQueries({ queryKey: ['experience'] });

      const profile = await refreshProfile();
      if (!profile?.delegated || profile.delegationId !== delegationId) {
        throw new Error('AUTH_DELEGATION_ENTRY_CONTEXT_MISMATCH');
      }
    } catch (error) {
      if (previousToken) setAccessToken(previousToken);
      queryClient.removeQueries({ queryKey: contextQueryKeyPrefix, exact: false });
      queryClient.removeQueries({ queryKey: ['navigation'] });
      queryClient.removeQueries({ queryKey: ['experience'] });
      await refreshProfile();
      throw error;
    }
  }, [queryClient, refreshProfile]);
}
